import React from 'react';
import { BaseListViewStore } from 'core/stores';
import { action, makeObservable } from 'mobx';
import { checkForColumnWidth, isSuperAdminOrganization, isSeller, isWarehouse } from 'core/utils';
import { localStorageHandler } from 'core/services';
import LinksStore from './LinksStore';
import { LinkAddEdit } from '../pages';

const URLCellData = ({ dataItem, field, rowType }) => {
	if (rowType === 'groupHeader') {
		return null;
	}
	const url = dataItem[field];
	return (
		<td>
			<a href={url} target="_blank" rel="noopener noreferrer">
				{url}
			</a>
		</td>
	);
};

class LinksViewStore extends BaseListViewStore {
	localStorageWidthKey = 'links-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new LinksStore(rootStore),
			findFunction: 'findLinks',
			tableData: {
				columns: [
					{
						key: 'description',
						title: 'Description',
						sortable: true,
					},
					{
						key: 'url',
						title: 'URL',
						sortable: true,
						dataCell: URLCellData,
					},
				],
				sortObject: [],
				actions: {
					onEdit: item => this.editLink(item),
					onDelete: item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
				},
			},
			permissions: {
				onEdit: () => isSuperAdminOrganization(rootStore),
				onDelete: () => isSuperAdminOrganization(rootStore),
			},
		});
		makeObservable(this);
		this.isSuperAdminOrganization = isSuperAdminOrganization(rootStore);
		this.isSeller = isSeller(rootStore);
		this.isWarehouse = isWarehouse(rootStore);
		this.initialize();
	}

	@action.bound
	findLinks = async () => {
		await this.listStore.findLinks(this.queryUtility.filter);
		let responseData = this.listStore.response;
		this.queryUtility.handleResponse(responseData);
	};

	@action.bound
	openAddLinkModal = () => {
		this.openModal({
			component: LinkAddEdit,
			data: {
				onSuccess: () => this.findLinks(),
			},
		});
	};

	@action.bound
	editLink = item => {
		this.openModal({
			component: LinkAddEdit,
			data: {
				id: item.id,
				description: item.description,
				url: item.url,
				onSuccess: () => this.findLinks(),
			},
		});
	};

	@action.bound
	deleteLink = async item => {
		this.suspend();
		try {
			await this.listStore.deleteLink(item.id);
			this.rootStore.notificationStore.success(['Link deleted successfully']);
			await this.findLinks();
		} catch (err) {
			this.rootStore.notificationStore.error(['Failed to delete link']);
		}
		this.resume();
	};

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => this.deleteLink(item),
			message: ['Are you sure you want to delete this link?'],
			data: item,
		};
	};

	@action.bound
	initialize = async () => {
		this.suspend();
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setHeaderModes();
		this.queryUtility.initialize(this.findLinks);
		this.resume();
	};
}

export default LinksViewStore;
